import type { ReactElement } from 'react'
import { Route, useParams } from 'react-router-dom'

import { LicenseGuard } from './guards'
import { lazyWithRetry } from './lazyWithRetry'
import { ROUTES } from './types'

/**
 * The Planning (Kebenaran Merancang) route block, kept out of `routes/index.tsx`
 * so that file stays under the 300-line limit. It is called as `{planningRoutes()}`
 * inside the AuthGuard + AppLayout layout route. React Router reads `<Route>`
 * ELEMENTS (arrays included), not components, so this must stay a function that
 * returns elements, never a `<PlanningRoutes />` component.
 *
 * Every path in planningRoutes() is behind LicenseGuard ['planning'] and carries no RoleGuard: the
 * read-only officers and the applicants are viewers, and the backend's requirePlanningActor decides
 * what each caller may do (spec section 5); the pages read GET /planning/me for the same answer.
 * `/planning` is the role-based landing (PlanningHomePage). The route manifests (route-manifest.ts
 * and the ai service's routeManifest.ts) model exactly these gates. `/planning/submissions/new`
 * outranks `:id` (a static segment wins). The public registration pages are a separate block,
 * planningPublicRoutes(), mounted OUTSIDE AuthGuard.
 */
const PlanningHomePage = lazyWithRetry(() => import('../pages/planning/PlanningHomePage'))
const SubmissionsListPage = lazyWithRetry(() => import('../pages/planning/SubmissionsListPage'))
const NewSubmissionPage = lazyWithRetry(() => import('../pages/planning/NewSubmissionPage'))
const SubmissionDetailPage = lazyWithRetry(() => import('../pages/planning/SubmissionDetailPage'))
const ActivityPage = lazyWithRetry(() => import('../pages/planning/ActivityPage'))
const RegistrationsPage = lazyWithRetry(() => import('../pages/planning/RegistrationsPage'))
const PlanningRegisterPage = lazyWithRetry(() => import('../pages/planning/public/PlanningRegisterPage'))
const PlanningRegisterStatusPage = lazyWithRetry(() => import('../pages/planning/public/PlanningRegisterStatusPage'))

/** The licence module gating every planning route; mirrors ModuleLabel.PLANNING. */
const PLANNING_MODULE = 'planning'

function guarded(page: ReactElement): ReactElement {
  return <LicenseGuard requiredLicenses={[PLANNING_MODULE]}>{page}</LicenseGuard>
}

/** Keyed by case id: `:id` changing in place (history, assistant navigation) must remount, not show the old case. */
function KeyedSubmissionDetail(): ReactElement {
  const { id = '' } = useParams()

  return <SubmissionDetailPage key={id} />
}

export function planningRoutes(): ReactElement[] {
  return [
    <Route key={ROUTES.PLANNING} path={ROUTES.PLANNING} element={guarded(<PlanningHomePage />)} />,
    <Route
      key={ROUTES.PLANNING_SUBMISSIONS}
      path={ROUTES.PLANNING_SUBMISSIONS}
      element={guarded(<SubmissionsListPage />)}
    />,
    <Route
      key={ROUTES.PLANNING_SUBMISSION_NEW}
      path={ROUTES.PLANNING_SUBMISSION_NEW}
      element={guarded(<NewSubmissionPage />)}
    />,
    <Route
      key={ROUTES.PLANNING_SUBMISSION}
      path={ROUTES.PLANNING_SUBMISSION}
      element={guarded(<KeyedSubmissionDetail />)}
    />,
    <Route
      key={ROUTES.PLANNING_REGISTRATIONS}
      path={ROUTES.PLANNING_REGISTRATIONS}
      element={guarded(<RegistrationsPage />)}
    />,
    <Route key={ROUTES.PLANNING_ACTIVITY} path={ROUTES.PLANNING_ACTIVITY} element={guarded(<ActivityPage />)} />,
  ]
}

/**
 * The public applicant registration pages (spec section 7.7), called as `{planningPublicRoutes()}` in
 * the public block of routes/index.tsx, OUTSIDE AuthGuard and AppLayout. There is no LicenseGuard:
 * without a session there are no licences to read. The backend answers 403 registration_closed when
 * the tenant is unlicensed, suspended, central on cloud, or has self-registration off, and the pages
 * say so. Not in the assistant's route manifest (NOT_NAVIGABLE in the parity spec).
 */
export function planningPublicRoutes(): ReactElement[] {
  return [
    <Route key={ROUTES.PLANNING_REGISTER} path={ROUTES.PLANNING_REGISTER} element={<PlanningRegisterPage />} />,
    <Route
      key={ROUTES.PLANNING_REGISTER_STATUS}
      path={ROUTES.PLANNING_REGISTER_STATUS}
      element={<PlanningRegisterStatusPage />}
    />,
  ]
}
